import api from '../api';

export const POST_VIEW_COUNT_UPDATED_EVENT = 'post:view-count-updated';

const MAX_BATCH_SIZE = 20;
const FLUSH_DELAY_MS = 800;
const MAX_RETRY_ATTEMPTS = 2;
const RETRY_BASE_DELAY_MS = 1500;

let activeViewerKey = null;
let sessionVersion = 0;
let flushTimer = null;

const queuedPostIds = new Set();
const submittedPostIds = new Set();
const retryAttempts = new Map();

const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

const clearFlushTimer = () => {
  if (!flushTimer) return;

  clearTimeout(flushTimer);
  flushTimer = null;
};

const resetTrackerSession = (viewerKey) => {
  clearFlushTimer();

  activeViewerKey = normalizeId(viewerKey) || null;
  sessionVersion += 1;

  queuedPostIds.clear();
  submittedPostIds.clear();
  retryAttempts.clear();
};

export const setPostViewTrackerViewer = (viewerKey) => {
  const normalizedViewerKey = normalizeId(viewerKey) || null;

  if (normalizedViewerKey !== activeViewerKey) {
    resetTrackerSession(normalizedViewerKey);
  }
};

const dispatchCountUpdate = (postId, viewCount) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(
    new CustomEvent(POST_VIEW_COUNT_UPDATED_EVENT, {
      detail: {
        postId: normalizeId(postId),
        viewCount: Math.max(0, Number(viewCount) || 0),
      },
    })
  );
};

const processAuthoritativeCounts = (counts) => {
  if (!counts) return;

  if (Array.isArray(counts)) {
    counts.forEach((item) => {
      const postId = item?.postId || item?._id || item?.id;
      const viewCount = item?.viewCount ?? item?.count;

      if (postId && Number.isFinite(Number(viewCount))) {
        dispatchCountUpdate(postId, viewCount);
      }
    });

    return;
  }

  if (typeof counts === 'object') {
    Object.entries(counts).forEach(([postId, value]) => {
      const viewCount =
        value && typeof value === 'object'
          ? value.viewCount ?? value.count
          : value;

      if (Number.isFinite(Number(viewCount))) {
        dispatchCountUpdate(postId, viewCount);
      }
    });
  }
};

const isTemporaryFailure = (error) => {
  const status = error?.response?.status;

  return (
    !error?.response ||
    status === 408 ||
    status === 425 ||
    status === 429 ||
    status >= 500
  );
};

const scheduleFlush = (delay = FLUSH_DELAY_MS) => {
  if (flushTimer || queuedPostIds.size === 0 || !activeViewerKey) return;

  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushPostViews();
  }, delay);
};

const requeueTemporaryFailures = (postIds, requestSessionVersion) => {
  if (
    requestSessionVersion !== sessionVersion ||
    !activeViewerKey
  ) {
    return;
  }

  let shortestRetryDelay = null;

  postIds.forEach((postId) => {
    const attempts = retryAttempts.get(postId) || 0;

    submittedPostIds.delete(postId);

    if (attempts >= MAX_RETRY_ATTEMPTS) {
      retryAttempts.delete(postId);
      return;
    }

    const nextAttempt = attempts + 1;
    const retryDelay = RETRY_BASE_DELAY_MS * 2 ** (nextAttempt - 1);

    retryAttempts.set(postId, nextAttempt);
    queuedPostIds.add(postId);

    if (shortestRetryDelay === null || retryDelay < shortestRetryDelay) {
      shortestRetryDelay = retryDelay;
    }
  });

  if (shortestRetryDelay !== null) {
    scheduleFlush(shortestRetryDelay);
  }
};

export const flushPostViews = async () => {
  clearFlushTimer();

  if (!activeViewerKey || queuedPostIds.size === 0) return;

  const requestSessionVersion = sessionVersion;
  const postIds = Array.from(queuedPostIds).slice(0, MAX_BATCH_SIZE);

  postIds.forEach((postId) => {
    queuedPostIds.delete(postId);
    submittedPostIds.add(postId);
  });

  try {
    const response = await api.post('/posts/views', { postIds });

    if (requestSessionVersion !== sessionVersion) return;

    const counts =
      response?.data?.counts ||
      response?.data?.viewCounts ||
      response?.data?.data?.counts;

    processAuthoritativeCounts(counts);

    postIds.forEach((postId) => {
      retryAttempts.delete(postId);
    });
  } catch (error) {
    if (requestSessionVersion !== sessionVersion) return;

    if (isTemporaryFailure(error)) {
      requeueTemporaryFailures(postIds, requestSessionVersion);
    } else {
      postIds.forEach((postId) => {
        retryAttempts.delete(postId);
      });
    }
  } finally {
    if (
      requestSessionVersion === sessionVersion &&
      queuedPostIds.size > 0
    ) {
      scheduleFlush();
    }
  }
};

export const queuePostView = (postId, viewerKey) => {
  const normalizedPostId = normalizeId(postId);
  const normalizedViewerKey = normalizeId(viewerKey);

  if (!normalizedPostId || !normalizedViewerKey) return false;

  setPostViewTrackerViewer(normalizedViewerKey);

  if (
    submittedPostIds.has(normalizedPostId) ||
    queuedPostIds.has(normalizedPostId)
  ) {
    return false;
  }

  queuedPostIds.add(normalizedPostId);

  if (queuedPostIds.size >= MAX_BATCH_SIZE) {
    flushPostViews();
  } else {
    scheduleFlush();
  }

  return true;
};

export const clearPostViewTracker = () => {
  resetTrackerSession(null);
};
